import {
  BaseEdge,
  EdgeLabelRenderer,
  type EdgeProps,
  getStraightPath,
  useReactFlow,
  useUpdateNodeInternals,
} from "@xyflow/react";
import { type JSX } from "react";

import { TrashIcon } from "@heroicons/react/24/outline";
import {
  checkContainsIOParameter,
  checkForOtherConnections,
  getIOParameterID,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { NODE_TYPES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import "../pipeline-graph.css";

export default function DeleteButtonEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style = {},
  markerEnd,
}: EdgeProps): JSX.Element {
  const updateNodeInternals = useUpdateNodeInternals();
  const { getEdges, setEdges, setNodes } = useReactFlow();
  const [edgePath, labelX, labelY] = getStraightPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });
  const onEdgeClick = (_, edgeId): void => {
    const edges = getEdges();
    const isIOParameter = checkContainsIOParameter(edgeId);
    const formatted = isIOParameter ? getIOParameterID(edgeId) : edgeId;
    const hasOtherConnections = checkForOtherConnections(edges, edgeId);

    setNodes((ns): any =>
      ns.map((node: any) => {
        if (
          formatted.type === NODE_TYPES.InputNode &&
          node.id === formatted?.nodeId
        ) {
          const outputHandle = hasOtherConnections
            ? node.data.outputHandles[0]
            : formatted?.newId;
          const outputTooltip = hasOtherConnections
            ? node.data.tooltips.outputs[0]
            : { connect: "Connect to Input" };
          return {
            ...node,
            data: {
              ...node.data,
              outputHandles: [outputHandle],
              tooltips: {
                inputs: [],
                outputs: [outputTooltip],
              },
              parameterId: formatted?.parameterId,
            },
          };
        } else if (
          formatted.type === NODE_TYPES.OutputNode &&
          node.id === formatted?.nodeId
        ) {
          return {
            ...node,
            data: {
              ...node.data,
              inputHandles: [formatted.newId],
              parameterId: formatted?.parameterId,
              tooltips: {
                inputs: [{ connect: "Connect to Output" }],
                outputs: [],
              },
            },
          };
        }
        return node;
      })
    );
    updateNodeInternals(formatted?.nodeId);
    setEdges((edges): any => {
      const result = edges.filter(edge => edge.id !== edgeId);
      return result;
    });
  };
  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 12,
            // everything inside EdgeLabelRenderer has no pointer events by default
            // if you have an interactive element, set pointer-events: all
            pointerEvents: "all",
          }}
          className="nodrag nopan">
          <button
            className={classNames(
              style.opacity === 0.2 ? "hidden" : "edge-button"
            )}
            onClick={e => onEdgeClick(e, id)}>
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
